import { Component, Input, OnInit, ChangeDetectorRef, OnChanges, SimpleChanges } from '@angular/core';
import { FormGroup, FormBuilder, Validators, AbstractControl } from '@angular/forms';
import { BasePaymentModalComponent } from '../base-payment-modal/base-payment-modal.component';

/**
 * @Component Decorator that marks a class as an Angular component and provides configuration metadata.
 * @description Component for handling check payment forms within a modal dialog. Extends BasePaymentModalComponent.
 */
@Component({
	selector: 'app-check-payment-form',
	templateUrl: './check-payment-form.component.html',
	styleUrls: ['./check-payment-form.component.scss']
})
export class CheckPaymentFormComponent extends BasePaymentModalComponent implements OnInit, OnChanges {
	/**
	 * @Input Decorator for master bank data.
	 */
	@Input() masterBank: any[] = [];
	/**
	 * @Input Decorator for all branch data.
	 */
	@Input() allBranches: any[] = [];

	/**
	 * @description Stores filtered branches for each line index.
	 */
	branchesForCheckLines: { [index: number]: any[] } = {};

	/**
	 * @constructor Initializes the component.
	 * @param {FormBuilder} fb Service for creating reactive forms.
	 * @param {ChangeDetectorRef} cdr Service for triggering change detection.
	 */
	constructor(
		protected fb: FormBuilder,
		protected cdr: ChangeDetectorRef
	) {
		super(fb, cdr);
	}

	/**
	 * @method ngOnInit Lifecycle hook.
	 * @description Initializes the component, checks for lines input, and initializes local lines.
	 */
	ngOnInit(): void {
		if (!this.lines) {
			console.error("CheckPaymentFormComponent requires a lines FormArray input.");
			this.lines = this.fb.array([]);
		}
		this.initLocalLines();

		this.updateBranchDisabledStateForAllLines();
	}

	/**
	 * @method ngOnChanges Lifecycle hook.
	 * @param {SimpleChanges} changes Object containing changed properties.
	 * @description Responds to changes in input properties.
	 */
	ngOnChanges(changes: SimpleChanges): void {
		let needsBranchUpdate = false;

		if (changes['masterBank']) {
			this.cdr.markForCheck();
		}
		if (changes['allBranches']) {
			needsBranchUpdate = true;
			this.cdr.markForCheck();
		}
		if (changes['lines']) {
			this.initLocalLines();
			needsBranchUpdate = true;
			this.cdr.markForCheck();
			this.updateBranchDisabledStateForAllLines();
		}

		if (needsBranchUpdate && this.localLines) {
			this.localLines.controls.forEach((_, index) => this.updateBranchesForLine(index));
		}
	}

	/**
	 * @method addCheckLine Adds a new, empty check line to the local form array.
	 * @param {boolean} [updateTotal=true] Whether to update the total display after adding the line.
	 */
	addCheckLine(updateTotal: boolean = true): void {
		if (!this.localLines) {
			this.initLocalLines();
		}
		this.addLineWithValidation(() => this.createEmptyLineGroup(), updateTotal);
	}

	/**
	 * @getter localLinesInvalid Checks if the local form array is invalid.
	 * @returns {boolean} True if the `localLines` FormArray is invalid, false otherwise.
	 */
	get localLinesInvalid(): boolean {
		return this.localLines?.invalid ?? true;
	}

	get bankOptionLabel(): string {
		return `bank_name_${this.curentLang || 'en'}`;
	}

	get branchFilterFields(): string {
		return `bank_branch_code,branch_name_${this.curentLang || 'en'}`;
	}

	/**
	 * @method onAmountInput Handles the input event for the amount field.
	 * @param {object} event The event object.
	 * @param {number} index The index of the line.
	 * @description Delegates to the base class method.
	 */
	onAmountInput(event: { originalEvent: Event, value: number | null }, index: number): void {
		this.onAmountInputLocal(event, index);
	}

	/**
	 * @method updateBranchesForLine Updates the filtered branch list for a specific line based on the selected bank.
	 * @param {number} index The index of the line.
	 */
	updateBranchesForLine(index: number): void {
		const bankCode = this.localLines.at(index)?.get('bank')?.value;
		this.filterBranches(bankCode, index);
	}

	/**
	 * @method onBankChange Handles the change event for the bank dropdown.
	 * @param {string | null} bankCode The selected bank code.
	 * @param {number} index The index of the line.
	 * @description Filters branches and resets the branch selection.
	 */
	onBankChange(bankCode: string | null, index: number): void {
		this.filterBranches(bankCode, index);
		const branchControl = this.localLines.at(index)?.get('branch');
		branchControl?.setValue(null);

		if (branchControl) {
			if (bankCode) {
				branchControl.enable({ emitEvent: false });
			} else {
				branchControl.disable({ emitEvent: false });
				branchControl.markAsTouched();
				branchControl.updateValueAndValidity({ emitEvent: false });
			}
		}

		const bankControl = this.localLines.at(index)?.get('bank');
		bankControl?.markAsTouched();
		bankControl?.updateValueAndValidity({ emitEvent: false });

		this.cdr.detectChanges();
	}

	/**
	 * @method filterBranches Filters the `allBranches` array based on the selected bank code for a specific line.
	 * @param {string | null} bankCode The bank code to filter by.
	 * @param {number} index The index of the line to store the filtered branches for.
	 */
	filterBranches(bankCode: string | null, index: number): void {
		if (!bankCode || !this.allBranches || this.allBranches.length === 0) {
			this.branchesForCheckLines[index] = [];
		} else {
			const bank = this.masterBank.find(b => String(b.bank_code) === String(bankCode));
			const bankId = bank ? bank.bank_id : null;
			this.branchesForCheckLines[index] = this.allBranches.filter(branch =>
				String(branch.bank_id) === String(bankId)
			);
		}
		}

	/**
	 * @method getBranchesForLine Retrieves the filtered list of branches for a specific line.
	 * @param {number} index The index of the line.
	 * @returns {any[]} The array of branches for the line, or an empty array.
	 */
	getBranchesForLine(index: number): any[] {
		const branches = this.branchesForCheckLines[index] || [];
		return branches;
	}

	/**
	 * @method getBankLabel Returns the appropriate bank name based on the current language.
	 * @param {any} item The bank item object.
	 * @returns {string} The localized bank name.
	 */
	getBankLabel(item: any): string {
		if (!item) return '';
		return this.curentLang === 'ar' ? item.bank_name_ar : (this.curentLang === 'he' ? item.bank_name_he : item.bank_name_en);
	}

	/**
	 * @method getBranchLabel Returns a formatted branch label including code and localized name.
	 * @param {any} item The branch item object.
	 * @returns {string} The formatted branch label.
	 */
	getBranchLabel(item: any): string {
		if (!item) return '';
			const name =
				this.curentLang === 'ar'
					? item.bank_branch_name_ar
					: this.curentLang === 'he'
					? item.bank_branch_name_he
					: item.bank_branch_name_en;
			return `${item.bank_branch_code} - ${name}`;
	}

	/**
	 * @method getLineControls Retrieves the form controls for a specific line.
	 * @param {number} index The index of the line.
	 * @returns {{ [key: string]: AbstractControl }} An object containing the form controls.
	 */
	getLineControls(index: number): { [key: string]: AbstractControl } {
		const lineGroup = this.localLines.at(index) as FormGroup;
		return lineGroup ? lineGroup.controls : {};
	}

	/**
	 * @method createEmptyLineGroup Creates a new FormGroup representing an empty check line.
	 * @returns {FormGroup} A FormGroup instance for a new check line.
	 */
	createEmptyLineGroup(): FormGroup {
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		const group = this.fb.group({
			id: [null],
			payment_type: [2],
			date: [today, Validators.required],
			amount: [null, [Validators.required, Validators.min(0.01)]],
			bank: [null],
			branch: [{ value: null, disabled: true }],
			account: [null],
			check: [null],
			details: [''],
		});
		const bankValue = group.get('bank')?.value;
		if (bankValue) {
			group.get('branch')?.enable({ emitEvent: false });
		}
		return group;
	}

	/**
	 * Enable/disable branch controls for all lines based on their bank value.
	 */
	updateBranchDisabledStateForAllLines(): void {
		if (!this.localLines) return;
		this.localLines.controls.forEach((ctrl, idx) => {
			const bankValue = ctrl.get('bank')?.value;
			const branchCtrl = ctrl.get('branch');
			if (branchCtrl) {
				if (bankValue) {
					branchCtrl.enable({ emitEvent: false });
				} else {
					branchCtrl.disable({ emitEvent: false });
				}
			}
		});
	}
}
